import { useRef, useState } from "react";
import axios from "axios";

import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";

import { useNavigate, useLocation } from "react-router-dom";

export default function DriverAppPaymentSubmit() {
  const navigate = useNavigate();
  const location = useLocation();

  const fileInputRef = useRef(null);

  const invoice = location.state?.invoice || null;

  const [paymentMode, setPaymentMode] = useState("UPI");
  const [referenceNo, setReferenceNo] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // =====================================
  // CLIENT ID
  // =====================================

 const clientId =
  invoice?.client_id ||
  localStorage.getItem("clientId") ||
  localStorage.getItem("trialClientId");

  // =====================================
  // INVOICE DETAILS
  // =====================================

  const invoiceId = invoice?.invoice_id;

  const invoiceNo = invoice?.invoice_no || "-";

  const amount = Number(
    invoice?.balance_amount ??
      invoice?.total_due ??
      invoice?.balance ??
      invoice?.amount_before_due ??
      0
  );

  const driverCount = Number(
    invoice?.driver_count || 0
  );

  const dueDate = invoice?.due_date
    ? new Date(invoice.due_date).toLocaleDateString("en-GB")
    : "-";

  const drivers = invoice?.drivers || [];

  // =====================================
  // SELECT FILE
  // =====================================

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setSelectedFile(file);
  };

  // =====================================
  // SUBMIT PAYMENT
  // =====================================

  const handleSubmitPayment = async () => {
    try {
      if (!clientId) {
        alert(
          "Client information not found. Please login again."
        );
        return;
      }

      if (!invoiceId) {
        alert(
          "Driver App invoice information not found."
        );
        return;
      }

      if (!referenceNo.trim()) {
        alert(
          "Please enter Reference / UTR Number"
        );
        return;
      }

      if (!selectedFile) {
        alert(
          "Please upload payment screenshot"
        );
        return;
      }

      setLoading(true);

      const formData = new FormData();

      formData.append(
        "invoice_id",
        invoiceId
      );

      formData.append(
        "client_id",
        Number(clientId)
      );

      formData.append(
        "payment_mode",
        paymentMode
      );

      formData.append(
        "reference_no",
        referenceNo.trim()
      );

      formData.append(
        "payment_amount",
        amount
      );

      formData.append(
        "screenshot",
        selectedFile
      );

      // =====================================
      // EXISTING PAYMENT API
      // =====================================

      const response = await axios.post(
        "/api/admin/subscription-payments",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      alert(
        response.data?.message ||
          "Driver App payment submitted successfully."
      );

      // Return to Dashboard
      navigate("/", {
        replace: true,
        state: {
          paymentSubmitted: true,
          driverAppPaymentSubmitted: true,
        },
      });

    } catch (error) {
      console.error(
        "❌ Driver App Payment Error:",
        error
      );

      console.error(
        "SERVER RESPONSE:",
        error?.response?.data
      );

      alert(
        error?.response?.data?.message ||
          "Failed to submit Driver App payment."
      );

    } finally {
      setLoading(false);
    }
  };

  // =====================================
  // NO INVOICE
  // =====================================

  if (!invoice) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#F6F7FB",
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 4,
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              color: "#6D28D9",
              mb: 2,
            }}
          >
            Driver App Payment
          </Typography>

          <Alert severity="warning">
            No pending Driver App invoice found.
          </Alert>

          <Button
            variant="contained"
            sx={{
              mt: 3,
              bgcolor: "#7C3AED",
              "&:hover": {
                bgcolor: "#6D28D9",
              },
            }}
            onClick={() => navigate("/")}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 20px)",
        overflowY: "auto",
        bgcolor: "#F6F7FB",
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >
      <Paper
        elevation={4}
        sx={{
          maxWidth: 750,
          mx: "auto",
          p: {
            xs: 2,
            sm: 3,
          },
          borderRadius: 3,
        }}
      >
        {/* HEADER */}

        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            color: "#6D28D9",
            mb: 1,
          }}
        >
          Driver App Payment
        </Typography>

        <Typography
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Submit your Driver App activation
          payment for administrator verification.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* INVOICE SUMMARY */}

        <Box
          sx={{
            bgcolor: "#F3E8FF",
            border: "1px solid #DDD6FE",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              color: "#5B21B6",
              mb: 2,
            }}
          >
            Driver App Invoice
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                color="text.secondary"
              >
                Invoice No
              </Typography>

              <Typography fontWeight="bold">
                {invoiceNo}
              </Typography>
            </Box>

            <Box>
              <Typography
                color="text.secondary"
              >
                Invoice Type
              </Typography>

              <Typography fontWeight="bold">
                DRIVER APP
              </Typography>
            </Box>

            <Box>
              <Typography
                color="text.secondary"
              >
                Drivers
              </Typography>

              <Typography fontWeight="bold">
                {driverCount}
              </Typography>
            </Box>

            <Box>
              <Typography
                color="text.secondary"
              >
                Due Date
              </Typography>

              <Typography fontWeight="bold">
                {dueDate}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
            >
              Amount Payable
            </Typography>

            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                color: "#6D28D9",
              }}
            >
              ₹
              {amount.toLocaleString(
                "en-IN",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </Typography>
          </Box>
        </Box>

        {/* DRIVER LIST */}

        {drivers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 1 }}
            >
              Driver App Activation
            </Typography>

            {drivers.map((driver, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                  border:
                    "1px solid #E2E8F0",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontWeight="bold">
                    {driver.driver_name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {driver.mobile}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    color: "#D97706",
                  }}
                >
                  PAYMENT PENDING
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* PAYMENT MODE */}

        <FormControl
          fullWidth
          sx={{ mb: 2 }}
        >
          <InputLabel>
            Payment Mode
          </InputLabel>

          <Select
            value={paymentMode}
            label="Payment Mode"
            onChange={(e) =>
              setPaymentMode(e.target.value)
            }
          >
            <MenuItem value="UPI">
              UPI
            </MenuItem>

            <MenuItem value="NEFT">
              NEFT
            </MenuItem>

            <MenuItem value="IMPS">
              IMPS
            </MenuItem>

            <MenuItem value="BANK TRANSFER">
              Bank Transfer
            </MenuItem>

            <MenuItem value="CASH">
              Cash
            </MenuItem>
          </Select>
        </FormControl>

        {/* REFERENCE */}

        <TextField
          fullWidth
          label="Reference / UTR Number"
          value={referenceNo}
          onChange={(e) =>
            setReferenceNo(e.target.value)
          }
          sx={{ mb: 2 }}
        />

        {/* SCREENSHOT */}

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ mb: 1 }}
          >
            Payment Screenshot
          </Typography>

          <Button
            variant="outlined"
            component="label"
            sx={{
              borderColor: "#7C3AED",
              color: "#6D28D9",
              "&:hover": {
                borderColor: "#6D28D9",
                bgcolor: "#F5F3FF",
              },
            }}
          >
            Select Screenshot

            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>

          {selectedFile && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "success.main",
              }}
            >
              Selected: {selectedFile.name}
            </Typography>
          )}
        </Box>

        {/* SUBMIT */}

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          onClick={handleSubmitPayment}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: "bold",
            bgcolor: "#7C3AED",
            "&:hover": {
              bgcolor: "#6D28D9",
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress
                size={22}
                sx={{
                  color: "#fff",
                  mr: 1,
                }}
              />

              SUBMITTING PAYMENT...
            </>
          ) : (
            "SUBMIT DRIVER APP PAYMENT"
          )}
        </Button>

        <Button
          fullWidth
          variant="text"
          sx={{
            mt: 1,
            color: "#64748B",
          }}
          onClick={() => navigate("/")}
        >
          Cancel / Back to Dashboard
        </Button>
      </Paper>
    </Box>
  );
}